import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Tag, Repeat, Sparkles, ExternalLink, Wifi, Loader2, Download } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";

/**
 * Partner Portal → Price list.
 *
 * Renders the exact plans the signup wizard sees, scoped to the
 * caller's partner_code — no drift risk because both views hit
 * ``internet.list_plans`` under the hood. Grouped by flow (transfer
 * vs new connection) and paged as clean cards; inactive plans stay
 * in the list but greyed out so partners can see what they used to
 * offer without pretending it's still on sale.
 */
export default function PartnerPriceList() {
  const [plans, setPlans] = useState(null);
  const [partnerCode, setPartnerCode] = useState("");
  const [staticIp, setStaticIp] = useState(null);
  const [downloading, setDownloading] = useState(false);
  // Tech filter — "" means show all. Clicking a chip narrows the price
  // list to plans available on that specific NBN technology so a
  // partner on a call can quickly answer "what can I sell for this
  // FTTC address?".
  const [techFilter, setTechFilter] = useState("");
  const NBN_TECHS = ["FTTP", "HFC", "FTTB", "FTTC", "FTTN", "FW"];

  async function downloadPdf() {
    setDownloading(true);
    try {
      const resp = await api.get("/partner/price-list.pdf", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${partnerCode || "partner"}-nbn-price-list.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Price list downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/partner/plans");
        setPlans(data.plans || []);
        setPartnerCode(data.partner_code || "");
        setStaticIp(data.static_ip_addon || null);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load price list");
        setPlans([]);
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    const g = { transfer: [], new: [], both: [] };
    const filtered = (plans || []).filter((p) => {
      if (!techFilter) return true;
      // Empty technologies list = "available on any tech" — always shown.
      if (!p.technologies || p.technologies.length === 0) return true;
      return p.technologies.includes(techFilter);
    });
    filtered.forEach((p) => {
      const key = (p.flow_type || "both").toLowerCase();
      (g[key] || g.both).push(p);
    });
    Object.values(g).forEach((rows) => rows.sort((a, b) => (a.monthly_price || 0) - (b.monthly_price || 0)));
    return g;
  }, [plans, techFilter]);

  const signupBase = `${window.location.origin}/signup/${encodeURIComponent(partnerCode || "")}`;

  return (
    <div className="space-y-8" data-testid="partner-price-list">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold mb-2 inline-flex items-center gap-1.5">
            <Tag className="w-3 h-3" /> Your price list
          </div>
          <h1 className="font-display text-3xl tracking-tight">Plans you can sell</h1>
          <p className="text-slate-400 mt-2 max-w-2xl text-sm">
            The exact pricing your customers see on their signup form &mdash; kept in sync automatically. Share your signup link below for real-time quoting.
          </p>
        </div>
        {partnerCode && (
          <div className="flex flex-col md:items-end gap-2 text-xs">
            <button
              type="button"
              data-testid="partner-price-download-pdf"
              onClick={downloadPdf}
              disabled={downloading || !plans || plans.length === 0}
              className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 px-4 py-2 font-semibold text-xs transition-colors"
            >
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              {downloading ? "Preparing PDF…" : "Download PDF"}
            </button>
            <div className="flex flex-col gap-1">
              <span className="text-slate-500 uppercase tracking-widest text-[10px]">Signup link</span>
              <a
                href={signupBase}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="partner-price-signup-link"
                className="inline-flex items-center gap-1.5 text-cyan-300 hover:text-cyan-200 font-mono"
              >
                {signupBase}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}
      </header>

      {staticIp?.enabled && (
        <div
          data-testid="static-ip-banner"
          className="rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-slate-900 px-5 py-4 flex items-start gap-4"
        >
          <div className="w-10 h-10 shrink-0 grid place-items-center rounded-full bg-amber-500/15 text-amber-300 font-mono text-sm">
            IP
          </div>
          <div>
            <div className="text-sm font-medium text-amber-200">
              Static IP available on any plan &mdash; +${(staticIp.monthly_price || 0).toFixed(2)}/mo
            </div>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl">
              Customers can opt in on the signup form. Perfect for hosted services, VPN, remote access, and CCTV. Applies on top of the plan price above.
            </p>
          </div>
        </div>
      )}

      {plans && plans.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="tech-filter-row">
          <span className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold">
            Filter by tech
          </span>
          <button
            type="button"
            data-testid="tech-filter-all"
            onClick={() => setTechFilter("")}
            className={
              "px-3 py-1 rounded-full font-semibold border transition-colors " +
              (techFilter === ""
                ? "bg-cyan-500/15 border-cyan-500/50 text-cyan-200"
                : "bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600")
            }
          >
            All
          </button>
          {NBN_TECHS.map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`tech-filter-${t}`}
              onClick={() => setTechFilter(techFilter === t ? "" : t)}
              className={
                "px-3 py-1 rounded-full font-mono font-semibold border transition-colors " +
                (techFilter === t
                  ? "bg-cyan-500/15 border-cyan-500/50 text-cyan-200"
                  : "bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600")
              }
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {plans === null && (
        <div className="text-slate-500 text-sm inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {plans && plans.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-800 bg-slate-950/40 p-10 text-center">
          <Wifi className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <div className="text-slate-300 font-medium mb-1">No plans configured yet</div>
          <div className="text-xs text-slate-500 max-w-md mx-auto">
            Ask your Steele Cloud account manager to publish your NBN plan pricing &mdash; it&rsquo;ll show up here and on your signup form automatically.
          </div>
        </div>
      )}

      {plans && plans.length > 0 && (
        <div className="space-y-10">
          {grouped.transfer.length > 0 && (
            <PlanGroup
              icon={Repeat}
              label="Transfer pricing"
              subtitle={
                <>
                  Transfer of <b>existing clients</b> only, due to price matching.
                  Pricing is <b>only valid for the agreed period</b> — customers
                  will need to transition to a standard plan once that
                  agreement ends.
                </>
              }
              testid="plan-group-transfer"
              plans={grouped.transfer}
            />
          )}
          {grouped.new.length > 0 && (
            <PlanGroup
              icon={Sparkles}
              label="New connections"
              subtitle="Fresh signups with no existing service to port."
              testid="plan-group-new"
              plans={grouped.new}
            />
          )}
          {grouped.both.length > 0 && (
            <PlanGroup
              icon={Wifi}
              label="Available on either flow"
              subtitle="Plans shown to both new + transferring customers."
              testid="plan-group-both"
              plans={grouped.both}
            />
          )}
        </div>
      )}
    </div>
  );
}

function PlanGroup({ icon: Icon, label, subtitle, plans, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-cyan-400" />
        <h2 className="font-medium text-slate-200">{label}</h2>
      </div>
      <p className="text-xs text-slate-500 mb-4">{subtitle}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {plans.map((p) => (
          <PlanCard key={p.id} plan={p} />
        ))}
      </div>
    </section>
  );
}

function PlanCard({ plan }) {
  const inactive = plan.active === false;
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={
        "rounded-xl border p-5 transition-colors " +
        (inactive
          ? "border-slate-800 bg-slate-900/40 opacity-60"
          : "border-slate-800 bg-slate-900 hover:border-cyan-500/40")
      }
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-1">
            {plan.speed_label || plan.speed_tier || "NBN"}
          </div>
          <div className="text-lg font-medium text-slate-100 leading-tight">
            {plan.name || "Untitled plan"}
          </div>
        </div>
        {inactive && (
          <span className="shrink-0 text-[10px] uppercase tracking-widest text-slate-500 font-semibold border border-slate-700 px-2 py-0.5 rounded-full">
            Off sale
          </span>
        )}
      </div>
      <div className="mb-2">
        <span className="text-2xl font-semibold text-cyan-300 font-mono">
          {money(plan.monthly_price || 0)}
        </span>
        <span className="text-slate-500 text-xs ml-1.5">/ month inc. GST</span>
      </div>
      {plan.description && (
        <p className="text-xs text-slate-400 leading-relaxed mt-3">
          {plan.description}
        </p>
      )}
      {(plan.technologies || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1" data-testid={`plan-tech-pills-${plan.id}`}>
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mr-1 mt-0.5">
            Techs
          </span>
          {plan.technologies.map((t) => (
            <span
              key={t}
              className="px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-[10px] font-mono font-semibold"
            >
              {t}
            </span>
          ))}
        </div>
      )}
      {plan.sku && (
        <div className="mt-3 pt-3 border-t border-slate-800 text-[10px] text-slate-600 font-mono truncate" title={plan.sku}>
          SKU: {plan.sku}
        </div>
      )}
    </div>
  );
}
